import { ClientContractChanges, ClientContractData, ContractType } from "@/data/models/ClientAdministrationRow";

/**
 * The Verträge edit of the IRES Admin Client (backlog #38, slice 2). Pure. IRES Cloud's
 * `updateClientAdministration` (group CONTRACTS) is the authority (`ClientContractFields.ts`); these
 * rules decide what the dialog sends.
 */

/** The contract types in the order the dialog offers them, as the detail lists them. */
export const CONTRACT_TYPE_ORDER: ContractType[] = [ContractType.PURCHASE, ContractType.PERMANENT_RENT, ContractType.TESTING];

/** The dialog's form; a start date is kept while its contract is unticked, so re-ticking restores it. */
export type ContractForm = ClientContractData;

/** The form for the stored values. */
export function toContractForm(stored: ClientContractData): ContractForm {
  return { ...stored, contractTypes: [...stored.contractTypes] };
}

function sorted(types: ContractType[]): ContractType[] {
  return Array.from(new Set(types)).sort();
}

/**
 * The request for `updateClientAdministration`: each changed field with the value loaded and the new
 * one; empty when nothing changed. An unticked contract sends its start date as null, so the dialog
 * says what IRES Cloud will store (it would clear it anyway).
 */
export function contractChanges(stored: ClientContractData, form: ContractForm): ClientContractChanges {
  const changes: ClientContractChanges = {};

  const types = sorted(form.contractTypes);
  if (types.join(",") !== sorted(stored.contractTypes).join(",")) {
    changes.contractTypes = { expected: stored.contractTypes, value: types };
  }

  const pairs = [
    ["hasServiceContractForFirmwaresAndConfigs", "hasServiceContractForFirmwaresAndConfigsSince"],
    ["hasWarrantyExtensionForPanels", "hasWarrantyExtensionForPanelsSince"],
  ] as const;
  for (const [flag, since] of pairs) {
    if (form[flag] !== stored[flag]) {
      changes[flag] = { expected: stored[flag], value: form[flag] };
    }
    const sinceValue = form[flag] ? form[since] : null;
    if (sinceValue !== stored[since]) {
      changes[since] = { expected: stored[since], value: sinceValue };
    }
  }

  return changes;
}

/** Whether the dialog may save: something changed. Every combination the form allows is valid. */
export function canSaveContracts(stored: ClientContractData, form: ContractForm): boolean {
  return Object.keys(contractChanges(stored, form)).length > 0;
}

/**
 * The i18n key of a refusal from IRES Cloud. Changed meanwhile, a missing client and an invalid field
 * read as for the Stammdaten; anything else is a failed Verträge save.
 */
export function contractsErrorKey(error: unknown): string {
  const failure = (error ?? {}) as { details?: { reason?: unknown } | null };
  const reason = typeof failure.details?.reason === "string" ? failure.details.reason : "";
  return ["CHANGED_MEANWHILE", "INVALID_VALUE", "NO_CLIENT"].includes(reason)
    ? `admin.clients.masterData.errors.${reason}`
    : "admin.clients.contracts.saveFailed";
}
